// Hộp hướng dẫn RIÊNG của trang Cơ chế lương.
//
// Vì sao chỉ trang này có bản riêng: bài của nó dài gấp nhiều lần bài trang
// khác (một màn, mười hai thẻ, mỗi thẻ một biểu giá), nên đổ tất cả vào một
// cột như `GuideDialog` là người đọc phải cuộn qua phần không liên quan để tới
// phần mình cần. Mọi trang khác vẫn dùng hộp chung — đừng nhân bản kiểu này
// cho trang thứ hai chỉ vì thấy đẹp.

import { useState } from "react";
import { HelpCircle } from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/ui/tabs";
import { useAuthV2 } from "@/lib/authv2/store";
import { DEPARTMENT_LABEL, DEPARTMENT_ORDER, type DepartmentKey } from "@/features/salary-rules-v2/types";

import { audiencesOf } from "./audience";
import { SALARY_PAY_FLOWS } from "./content/salary-pay-flow";
import { PayFlowDiagram } from "./PayFlowDiagram";
import { SALARY_GUIDE_TABS } from "./salary-guide-tabs";
import { GuideBlockList, GuideBulletList, GuideStatusList } from "./GuideSections";
import type { PageGuide } from "./types";

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  title: string;
  guide: PageGuide;
  /**
   * Bỏ qua ở hộp này — thẻ của nó là thẻ của BÀI (dòng tiền, cách chạy…),
   * không phải thẻ của màn. Nhận vào để ba hộp cùng một chữ ký.
   */
  guideTab?: string | undefined;
  /** Bỏ qua ở hộp này; nhận vào cho cùng chữ ký với hộp nhiều thẻ. */
  guideId?: string | undefined;
};

const FLOW_BY_DEPT = new Map(SALARY_PAY_FLOWS.map((f) => [f.dept, f]));

export function SalaryGuideDialog({ open, onOpenChange, title, guide }: Props) {
  const staff = useAuthV2((s) => s.currentStaff);
  const auds = audiencesOf(staff);
  const blocks = (guide.blocks ?? []).filter((b) => b.for.some((a) => auds.includes(a)));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="md:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="size-5 shrink-0 text-muted" aria-hidden />
            <span className="min-w-0">Hướng dẫn: {title}</span>
          </DialogTitle>
        </DialogHeader>

        <p className="rounded-md bg-muted/10 p-3 text-sm leading-relaxed">{guide.purpose}</p>

        {/*
          `min-w-0` không phải trang trí: thân hộp thoại là lưới, mà ô lưới mặc
          định không co nhỏ hơn nội dung của nó. Hàng thẻ bên trong giữ chữ trên
          một dòng, nên min-content của nó là TỔNG chiều rộng năm thẻ — kéo cả
          cột rộng ra và đẩy mọi đoạn văn tràn khỏi màn điện thoại. Đo ở khổ
          375px: bỏ lớp này là hộp rộng 598px và chữ bị cắt mất bên phải.
        */}
        <Tabs defaultValue="flow" className="min-w-0 text-sm">
          <TabsList aria-label="Phần hướng dẫn">
            <TabsTrigger value="flow">{SALARY_GUIDE_TABS.flow}</TabsTrigger>
            <TabsTrigger value="where">{SALARY_GUIDE_TABS.where}</TabsTrigger>
            <TabsTrigger value="how">{SALARY_GUIDE_TABS.how}</TabsTrigger>
            {blocks.length > 0 && <TabsTrigger value="work">{SALARY_GUIDE_TABS.work}</TabsTrigger>}
            <TabsTrigger value="status">{SALARY_GUIDE_TABS.status}</TabsTrigger>
          </TabsList>

          <TabsContent value="flow" className="space-y-4 pt-4">
            <PayFlowTab />
          </TabsContent>

          <TabsContent value="where" className="pt-4">
            <GuideBulletList items={guide.where ?? []} />
          </TabsContent>

          <TabsContent value="how" className="pt-4">
            <GuideBulletList items={guide.howItWorks ?? []} />
          </TabsContent>

          {blocks.length > 0 && (
            <TabsContent value="work" className="space-y-5 pt-4">
              <GuideBlockList blocks={blocks} />
            </TabsContent>
          )}

          <TabsContent value="status" className="pt-4">
            <GuideStatusList statuses={guide.statuses ?? []} labelWidth="sm:w-52" />
          </TabsContent>
        </Tabs>

        <p className="border-t border-border pt-3 text-sm text-muted">
          {guide.askWho ?? "Còn vướng chỗ nào thì hỏi quản lý trực tiếp của bạn."}
        </p>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Thẻ dòng tiền: chọn một thẻ của màn rồi xem tiền thẻ đó ra từ đâu.
 *
 * Mở sẵn thẻ đầu tiên — đúng thẻ trang lương cũng mở sẵn, nên hướng dẫn và màn
 * nói về cùng một thẻ ngay khi người ta vừa bấm nút.
 */
function PayFlowTab() {
  const [dept, setDept] = useState<DepartmentKey>(DEPARTMENT_ORDER[0]);
  const flow = FLOW_BY_DEPT.get(dept);

  return (
    <>
      <div className="space-y-1.5">
        <p className="text-muted">
          Chọn thẻ để xem tiền của thẻ đó ra từ đâu, và ô nào trên màn nuôi khoản nào.
        </p>
        <Select value={dept} onValueChange={(v) => setDept(v as DepartmentKey)}>
          <SelectTrigger aria-label="Chọn thẻ để xem dòng tiền">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DEPARTMENT_ORDER.map((d) => (
              <SelectItem key={d} value={d}>
                {DEPARTMENT_LABEL[d]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {flow && <PayFlowDiagram flow={flow} />}
    </>
  );
}
